import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Search, Users, Briefcase, Target } from 'lucide-react'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import FourFactorSection from '@/components/home/FourFactorSection'
import FinalCTA from '@/components/home/FinalCTA'

const lineV = {
  hidden: { y: '115%' },
  visible: (i) => ({
    y: '0%',
    transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1], delay: 0.05 + i * 0.1 },
  }),
}

const services = [
  {
    num: '01',
    title: 'Tech Leadership Search',
    tagline: 'CXO · VP · Director-level',
    description:
      'We identify and place transformational technology leaders across GCCs and product companies. From Chief Technology Officers to Staff Engineers — every search is retained, every shortlist is evidence-backed.',
    whatWePlace: [
      'Chief Technology Officer / VP Engineering',
      'Head of Data Science / Chief Data Officer',
      'Director of Product / VP Product',
      'Principal & Staff Engineers',
      'Head of Platform, Infrastructure, SRE',
    ],
    href: '/contact',
  },
  {
    num: '02',
    title: 'GCC Talent Setup & Scaling',
    tagline: 'Centre inception to 200+ headcount',
    description:
      'From founding hire to capability scale — we partner with GCCs at every stage of their journey. Assess the operating model, establish the leadership core, build the capability teams, and scale with intent.',
    whatWePlace: [
      'Founding Leader Placement',
      'Team Architecture Advisory',
      'Capability Ramp Planning',
      '4-Factor Assessment',
      'Compensation Benchmarking',
    ],
    href: '/gcc-practice',
  },
]

const process = [
  {
    num: '01',
    title: 'Brief Interrogation',
    desc: 'We challenge the mandate before we open it. Why this role? What does success look like in 12 months? We reshape briefs that would produce the wrong hire.',
    icon: Briefcase,
  },
  {
    num: '02',
    title: 'Market Mapping',
    desc: 'We build a target list from first principles — not from a database filter. We map who is doing this role well, where, and at what level.',
    icon: Search,
  },
  {
    num: '03',
    title: 'Direct Approach',
    desc: 'The best candidates rarely apply. We approach them — because we have earned the trust to have honest conversations about what they actually want.',
    icon: Target,
  },
  {
    num: '04',
    title: 'Structured Assessment',
    desc: 'Every shortlist is assessed against our Four-Factor framework — AQ, IQ, SQ, EQ — not just role fit, but organisational and contextual fit.',
    icon: Users,
  },
]

export default function ServicesPage() {
  return (
    <main data-testid="services-page">
      <Navbar />

      {/* Hero */}
      <section className="min-h-[70vh] bg-[#0A0A0A] flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-20 pt-36 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-px bg-white/5 hidden lg:block" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-white/8" />
        <motion.div initial="hidden" animate="visible" className="max-w-7xl relative z-10">
          <div className="overflow-hidden mb-6">
            <motion.p custom={0} variants={lineV}
              className="text-[10px] tracking-[0.38em] uppercase font-body font-semibold text-white/30">
              What We Do
            </motion.p>
          </div>
          <h1 className="mb-8">
            <div className="overflow-hidden">
              <motion.div custom={1} variants={lineV}
                className="text-[clamp(3rem,7vw,7.5rem)] font-heading font-black tracking-[-0.03em] leading-none text-white">
                Two practices.
              </motion.div>
            </div>
            <div className="overflow-hidden">
              <motion.div custom={2} variants={lineV}
                className="text-[clamp(3rem,7vw,7.5rem)] font-heading font-light tracking-[-0.03em] leading-none text-white/35">
                One standard.
              </motion.div>
            </div>
          </h1>
          <div className="overflow-hidden">
            <motion.p custom={3} variants={lineV}
              className="text-base md:text-lg text-white/45 max-w-xl font-body leading-relaxed">
              Tech Leadership Search and GCC Talent Setup & Scaling — each focused on the specific demands of building world-class technology organisations.
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* Services detail */}
      <section
        data-testid="services-detail-section"
        className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-white"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {services.map((svc, i) => (
              <motion.div
                key={svc.num}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 }}
                data-testid={`services-detail-${i + 1}`}
                className={`py-16 pr-0 lg:pr-16 ${i === 0 ? 'lg:border-r border-[#E5E7EB]' : 'lg:pl-16 lg:pr-0'} border-b lg:border-b-0 border-[#E5E7EB]`}
              >
                <p className="font-heading font-black text-[#E5E7EB] leading-none mb-8 select-none"
                  style={{ fontSize: 'clamp(4rem,7vw,6rem)' }}>
                  {svc.num}
                </p>
                <p className="text-[9px] tracking-[0.3em] uppercase text-[#16365F] mb-3 font-body font-semibold">
                  {svc.tagline}
                </p>
                <h2 className="text-2xl md:text-3xl font-heading font-black text-[#0A0A0A] mb-5 tracking-tight leading-tight">
                  {svc.title}
                </h2>
                <p className="text-sm font-body text-[#0A0A0A]/55 leading-relaxed mb-8 max-w-md">
                  {svc.description}
                </p>
                <div className="mb-10">
                  <p className="text-[9px] tracking-[0.25em] uppercase text-[#0A0A0A]/25 font-body font-semibold mb-4">
                    What we place
                  </p>
                  <ul className="space-y-2.5">
                    {svc.whatWePlace.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <div className="w-1 h-1 bg-[#0B1F3A] mt-2 flex-shrink-0" />
                        <span className="text-sm font-body text-[#0A0A0A]/55">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to={svc.href}
                  data-testid={`service-detail-cta-${i + 1}`}
                  className="inline-flex items-center gap-2 text-[10px] font-body font-semibold tracking-[0.15em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A]/30 pb-0.5 hover:text-[#0B1F3A] hover:border-[#0B1F3A] transition-colors duration-200"
                >
                  {i === 0 ? 'Start a search' : 'Explore GCC practice'} <ArrowRight size={11} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section
        data-testid="how-we-work-section"
        className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-[#F6F7F9]"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-20"
          >
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#0A0A0A]/30 mb-5 font-body font-semibold">
              The Process
            </p>
            <h2 className="text-[clamp(2.5rem,5vw,5rem)] font-heading font-black tracking-tighter leading-none text-[#0A0A0A]">
              How a search<br />
              <span className="font-light text-[#16365F]">actually works.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-[#E5E7EB]">
            {process.map((step, i) => {
              const Icon = step.icon
              return (
                <motion.div
                  key={step.num}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
                  data-testid={`process-step-${i + 1}`}
                  className="py-12 pr-0 lg:pr-10 border-b md:border-b-0 lg:border-r border-[#E5E7EB] last:border-r-0"
                >
                  <p className="text-[9px] tracking-[0.3em] uppercase text-[#0A0A0A]/20 font-body font-semibold mb-6">
                    {step.num}
                  </p>
                  <Icon size={18} className="text-[#16365F] mb-5" strokeWidth={1.5} />
                  <h3 className="text-base font-heading font-black text-[#0A0A0A] mb-3 tracking-tight">
                    {step.title}
                  </h3>
                  <p className="text-sm font-body text-[#0A0A0A]/50 leading-relaxed">
                    {step.desc}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      <FourFactorSection />
      <FinalCTA />
      <Footer />
    </main>
  )
}
